import React, { Fragment } from 'react'
import classNamesBind from 'classnames/bind'

import ProfileCTA from '../ProfileCTA'
import Section from '@dashboard/components/PublicProfile/Section'
import EnneagramModule from '@dashboard/components/EnneagramModule'
import MyersBriggsModule from '@dashboard/components/MyersBriggsModule'
import CircumplexGraphModule from '@dashboard/components/CircumplexGraphModule'
import ShareActions from '@dashboard/components/ShareActions'

import {
  Profile,
  Datum,
  TestResultsProfile,
  ProfileContent,
} from '@dashboard/lib/types'
import { fullName, isProfile } from '@dashboard/lib/profile'
import { convertDataTags } from '@dashboard/lib/api'
import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

interface OwnProps {
  profile: Profile | TestResultsProfile | null
  content?: ProfileContent | null
  id?: string
  signUpCta?: {
    text: string
    href?: string
    showSampleLink?: boolean
    onClick?: () => void
  }
  customModules?: JSX.Element
  behaviorPhrases?: Datum[] | null
}

interface State {
  showShareIcons: boolean
}

export default class PublicProfileContent extends React.Component<
  OwnProps,
  State
> {
  timeout: number | null = null

  constructor(props: OwnProps) {
    super(props)

    this.state = {
      showShareIcons: false,
    }
  }

  componentDidMount() {
    document.addEventListener('scroll', this.scrollHandler)
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.scrollHandler)

    if (this.timeout) clearTimeout(this.timeout)
  }

  render() {
    const { content, signUpCta, behaviorPhrases, profile } = this.props

    if (!profile) return null

    const contentBehaviorPhrases = content
      ? content[convertDataTags(['behavior', 'phrase'])]
      : []

    return (
      <div className={classNames('container', { public: true })}>
        {this.renderShareActions()}
        {this.renderCustomModules()}
        {this.renderAssessmentModules()}
        {this.renderBehaviorPhrases(
          profile,
          behaviorPhrases || contentBehaviorPhrases || [],
        )}
        {this.renderCircumplex()}

        <ProfileCTA cta={signUpCta} />
      </div>
    )
  }

  renderBehaviorPhrases(
    profile: Profile | TestResultsProfile,
    phrases: Datum[],
  ) {
    if (phrases.length === 0) return null

    const { id, signUpCta } = this.props
    const discType =
      profile && profile.personality ? profile.personality.disc_type : 'empty'
    const name = profile.first_name ? profile.first_name : profile.last_name

    return (
      <Section
        key="behavior"
        icon={'profile'}
        title={`What comes naturally to ${name}`}
        paywallText={` to learn what comes naturally to ${name}`}
        paywallCta={signUpCta}
        discType={discType}
        content={phrases}
        id={id}
      />
    )
  }

  renderShareActions() {
    const { profile } = this.props
    const { showShareIcons } = this.state

    if (!profile || !(isProfile(profile) && profile.type === 'celebrity'))
      return null

    return (
      <div className={css.shareActionsContainer}>
        <ShareActions
          active={showShareIcons}
          tweet={{
            text: profile
              ? `Check out ${fullName(
                  profile,
                )}’s personality on Crystal <url> via @crystalknowsme`
              : '',
          }}
        />
      </div>
    )
  }

  renderCustomModules() {
    const { customModules } = this.props

    return customModules || null
  }

  renderAssessmentModules() {
    const { profile } = this.props

    if (profile && profile.personality) {
      return (
        <Fragment>
          <EnneagramModule profile={profile} />
          <MyersBriggsModule profile={profile} />
        </Fragment>
      )
    }
  }

  renderCircumplex() {
    const { profile, content } = this.props

    if (profile && profile.personality && content) {
      const profileOverview = content
        ? (content[convertDataTags(['behavior', 'phrase'])] || [])[0]?.text
        : ''

      return (
        <CircumplexGraphModule
          profiles={[profile]}
          overview={profileOverview}
        />
      )
    }
  }

  // Functions
  initFacebook() {
    if (document.querySelector('#facebook-jssdk')) return

    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src =
      'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.0'
    script.async = true
    document.body.appendChild(script)

    type WindowWithFB = Window & { fbAsyncInit: () => void }

    const otherWindow = window as unknown as WindowWithFB
    otherWindow.fbAsyncInit = function () {
      window.FB.XFBML.parse()
    }
  }

  scrollHandler = () => {
    const { showShareIcons } = this.state

    const active = window.scrollY > 400

    if (active !== showShareIcons) this.setState({ showShareIcons: active })
  }
}
